import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { GridController, type CellFormat, type CellMetadataProjection } from '../../compositor/GridController'
import type { SpreadsheetFindMatch, SpreadsheetMutationError } from '../../data/ViewportStreamer'
import { AppHTMLHeaders } from '../AppHTMLHeaders'
import type { ExcelVisualizeCommandHandlers } from './ExcelRibbon'
import { ExcelSheetTabs } from './ExcelSheetTabs'
import {
  buildSpreadsheetEngineSheetId,
  type SpreadsheetCellAddress,
  type SpreadsheetDefinedNameEntry,
  type SpreadsheetDefinedNameScope,
  type SpreadsheetDefinedNamesResponsePayload,
  type SpreadsheetNamedRangesResponsePayload,
  type SpreadsheetRangeAddress,
  type SpreadsheetSheetProtectionOptions,
  type SpreadsheetValidationDropdownOptionsResponsePayload,
  type SpreadsheetValidationRule,
  type SpreadsheetValidationRulesResponsePayload,
} from '../../protocol/spreadsheetEngineProtocol'
import './ExcelWorkbookChrome.css'

const FIXTURE_HEADERS = [
  'Region',
  'Rep',
  'Product',
  'Q1',
  'Q2',
  'Q3',
  'Q4',
  'Total',
  'Forecast',
  'Notes',
  '',
  '',
]

const FIXTURE_ROWS = [
  ['West', 'Avery', 'Widgets', 1240, 1430, 1550, 1630, 5850, 6100, 'On track', '', ''],
  ['East', 'Morgan', 'Gears', 980, 1120, 1180, 1210, 4490, 4700, 'Needs review', '', ''],
  ['South', 'Riley', 'Bolts', 1410, 1495, 1510, 1680, 6095, 6320, 'Strong', '', ''],
  ['North', 'Casey', 'Panels', 760, 820, 910, 950, 3440, 3590, '', '', ''],
  ['', '', '', '', '', '', '', '', '', '', '', ''],
  ['Formula', '=SUM(D2:G5)', '', '', '', '', '', '=XLOOKUP(B3,B2:B5,H2:H5)', '', '', '', ''],
]

const BACKGROUND_MAP_OPTIONS = ['normal', 'light', 'dark', 'streets', 'outdoors', 'satellite'] as const
const LIVE_FIXTURE_SHEET_ID = buildSpreadsheetEngineSheetId(1)
const LIVE_FIXTURE_PRODUCT_VALIDATION_RULE: SpreadsheetValidationRule = {
  id: 'fixture-product-list',
  kind: 'list',
  allow_blank: false,
  values: ['Widgets', 'Gears', 'Bolts', 'Panels'],
  input_title: 'Product',
  input_message: 'Choose a product from the approved workbook list.',
  error_style: 'warning',
  error_title: 'Unlisted product',
  error_message: 'Choose one of Widgets, Gears, Bolts, or Panels.',
}
const LIVE_FIXTURE_REGION_VALIDATION_RULE: SpreadsheetValidationRule = {
  id: 'fixture-region-named-list',
  kind: 'list',
  allow_blank: false,
  formula1: '=RegionOptions',
  input_title: 'Region',
  input_message: 'Choose a region from the named workbook list.',
  error_style: 'information',
  error_title: 'Region not in list',
  error_message: 'Choose one of West, East, South, or North.',
}
const LIVE_FIXTURE_VALIDATION_OPTIONS: Record<string, string[]> = {
  [LIVE_FIXTURE_PRODUCT_VALIDATION_RULE.id]: ['Widgets', 'Gears', 'Bolts', 'Panels'],
  [LIVE_FIXTURE_REGION_VALIDATION_RULE.id]: ['West', 'East', 'South', 'North'],
}
const LIVE_FIXTURE_REGION_SOURCE_RANGES: SpreadsheetRangeAddress[] = [{
  sheet_id: LIVE_FIXTURE_SHEET_ID,
  start_row: 1,
  start_col: 0,
  end_row: 4,
  end_col: 0,
}]

const parseFixtureTsv = (text: string): string[][] => {
  const rows = String(text ?? '').split(/\r\n|\n|\r/)
  if (rows.length > 0 && rows[rows.length - 1] === '') rows.pop()
  return rows.map((line) => line.split('\t'))
}

class ExcelLiveFixtureStreamer {
  private callback: (() => void) | null = null
  private validationRulesCallback: ((payload: SpreadsheetValidationRulesResponsePayload) => void) | null = null
  private namedRangesCallback: ((payload: SpreadsheetNamedRangesResponsePayload) => void) | null = null
  private definedNamesCallback: ((payload: SpreadsheetDefinedNamesResponsePayload) => void) | null = null
  private mutationErrorCallback: ((error: SpreadsheetMutationError) => void) | null = null
  private findCallback: ((matches: SpreadsheetFindMatch[]) => void) | null = null
  private cells = new Map<string, string>()
  private formats = new Map<string, CellFormat>()
  private metadata = new Map<string, CellMetadataProjection>([
    ['9,1', {
      comment: 'Forecast owner: review variance before publishing.',
      hyperlink: 'https://fusegrid.local/workbooks/revenue-model/forecast',
      protection: { locked: true },
    }],
    ['8,4', {
      comment: 'North forecast is editable while the sheet is protected.',
      protection: { locked: false },
    }],
  ])
  private definedNames: SpreadsheetDefinedNameEntry[] = [{
    name: 'RegionOptions',
    scope: { kind: 'workbook' },
    refers_to: '=A2:A5',
    range: {
      sheet_id: LIVE_FIXTURE_SHEET_ID,
      start_row: 1,
      start_col: 0,
      end_row: 4,
      end_col: 0,
    },
  }]
  private rowCount = 1000
  private colCount = 12
  private readonly sheetProtectionOptions: SpreadsheetSheetProtectionOptions | null

  constructor(sheetProtectionOptions: SpreadsheetSheetProtectionOptions | null = null) {
    this.sheetProtectionOptions = sheetProtectionOptions
    FIXTURE_HEADERS.forEach((header, col) => {
      this.cells.set(this.key(col, 0), String(header ?? ''))
      this.formats.set(this.key(col, 0), {
        bold: true,
        backgroundColor: '#f6f1ea',
        textColor: '#3a3228',
      })
    })
    FIXTURE_ROWS.forEach((row, rowIndex) => {
      row.forEach((value, col) => {
        const text = value == null ? '' : String(value)
        if (text) this.cells.set(this.key(col, rowIndex + 1), text)
      })
    })
  }

  private key(col: number, row: number) {
    return `${Math.max(0, Math.floor(Number(col) || 0))},${Math.max(0, Math.floor(Number(row) || 0))}`
  }

  private validationRulesPayload(): SpreadsheetValidationRulesResponsePayload {
    return {
      requestId: 'fixture-validation-rules',
      workbookId: 'active_workbook',
      sheetId: LIVE_FIXTURE_SHEET_ID,
      workbookRevision: 1,
      validation_rules: [LIVE_FIXTURE_PRODUCT_VALIDATION_RULE, LIVE_FIXTURE_REGION_VALIDATION_RULE],
    }
  }

  private namedRangesPayload(): SpreadsheetNamedRangesResponsePayload {
    return {
      requestId: 'fixture-named-ranges',
      workbookId: 'active_workbook',
      sheetId: LIVE_FIXTURE_SHEET_ID,
      workbookRevision: 1,
      named_ranges: this.definedNames
        .filter((entry): entry is SpreadsheetDefinedNameEntry & { range: SpreadsheetRangeAddress } => Boolean(entry.range))
        .map((entry) => ({ name: entry.name, range: entry.range })),
      sheets: [{
        id: 1,
        name: 'Sales Model',
        row_count: this.rowCount,
        col_count: this.colCount,
        ...(this.sheetProtectionOptions ? { sheet_protection: this.sheetProtectionOptions } : null),
      }],
    }
  }

  private definedNamesPayload(): SpreadsheetDefinedNamesResponsePayload {
    return {
      requestId: 'fixture-defined-names',
      workbookId: 'active_workbook',
      sheetId: LIVE_FIXTURE_SHEET_ID,
      workbookRevision: 1,
      defined_names: this.definedNames,
      sheets: [{
        id: 1,
        name: 'Sales Model',
        row_count: this.rowCount,
        col_count: this.colCount,
        ...(this.sheetProtectionOptions ? { sheet_protection: this.sheetProtectionOptions } : null),
      }],
    }
  }

  private isProductValidationCell(col: number, row: number) {
    return col === 2 && row >= 1 && row <= 4
  }

  private validationRuleForCell(col: number, row: number): SpreadsheetValidationRule | undefined {
    if (this.isProductValidationCell(col, row)) return LIVE_FIXTURE_PRODUCT_VALIDATION_RULE
    if (col === 0 && row === 1) return LIVE_FIXTURE_REGION_VALIDATION_RULE
    return undefined
  }

  private mergeCellMetadata(col: number, row: number, patch: Partial<CellMetadataProjection>) {
    const key = this.key(col, row)
    const next = { ...(this.metadata.get(key) ?? {}) }
    if ('comment' in patch) {
      const comment = String(patch.comment ?? '').trim()
      if (comment) next.comment = comment
      else delete next.comment
    }
    if ('hyperlink' in patch) {
      const hyperlink = String(patch.hyperlink ?? '').trim()
      if (hyperlink) next.hyperlink = hyperlink
      else delete next.hyperlink
    }
    if ('protection' in patch) {
      if (patch.protection) next.protection = { ...patch.protection }
      else delete next.protection
    }
    if ('validationId' in patch) {
      if (patch.validationId) next.validationId = patch.validationId
      else delete next.validationId
    }
    if ('metadata' in patch) {
      if (patch.metadata) next.metadata = { ...patch.metadata }
      else delete next.metadata
    }
    if (Object.keys(next).length > 0) this.metadata.set(key, next)
    else this.metadata.delete(key)
  }

  private firstLockedCellInRange(startCol: number, startRow: number, endCol: number, endRow: number): SpreadsheetCellAddress | null {
    for (let row = Math.min(startRow, endRow); row <= Math.max(startRow, endRow); row += 1) {
      for (let col = Math.min(startCol, endCol); col <= Math.max(startCol, endCol); col += 1) {
        const protection = this.getCellMetadata(col, row)?.protection
        if (protection?.locked === false) {
          continue
        }
        if ((protection && protection.locked !== false) || this.sheetProtectionOptions?.enabled === true) {
          return { sheet_id: LIVE_FIXTURE_SHEET_ID, row, col }
        }
      }
    }
    return null
  }

  private emitProtectedCellEdit(address: SpreadsheetCellAddress, operationLabel: string, attemptedValue?: string) {
    this.mutationErrorCallback?.({
      requestId: `fixture_protected_${Date.now()}`,
      message: 'The cell you are trying to change is protected.',
      col: address.col,
      row: address.row,
      operationLabel,
      protectedCell: address,
      ...(attemptedValue != null ? { value: attemptedValue } : null),
    })
  }

  setCallback(callback: () => void) {
    this.callback = callback
  }

  setViewportUpdater() {}
  setViewport() {}
  updateViewport() {}
  setScroll() {}
  setSelection() {}
  switchSheet() {}

  setDimensions(cols: number, rows: number) {
    this.colCount = Math.max(1, Math.floor(Number(cols) || this.colCount))
    this.rowCount = Math.max(1, Math.floor(Number(rows) || this.rowCount))
  }

  setFrozenPanes() {
    this.callback?.()
  }

  getActiveSheetId() {
    return 1
  }

  getWorkerActiveSheetId() {
    return 1
  }

  getCellValue(col: number, row: number): string | undefined {
    return this.cells.get(this.key(col, row))
  }

  getCellFormula(col: number, row: number): string | undefined {
    const value = this.getCellValue(col, row)
    return value?.startsWith('=') ? value : undefined
  }

  getCellFormat(col: number, row: number): CellFormat | undefined {
    return this.formats.get(this.key(col, row))
  }

  getCellMetadata(col: number, row: number): CellMetadataProjection | undefined {
    const rule = this.validationRuleForCell(col, row)
    const metadata = this.metadata.get(this.key(col, row))
    if (!rule && !metadata) return undefined
    return {
      ...(metadata ?? {}),
      ...(rule ? { validationId: rule.id } : {}),
    }
  }

  setCellComment(col: number, row: number, comment: string | null) {
    this.mergeCellMetadata(col, row, { comment: comment ?? undefined })
    this.callback?.()
  }

  setCellHyperlink(col: number, row: number, hyperlink: string | null) {
    this.mergeCellMetadata(col, row, { hyperlink: hyperlink ?? undefined })
    this.callback?.()
  }

  setRangeProtection(startCol: number, startRow: number, endCol: number, endRow: number, protection: Record<string, unknown> | null) {
    for (let row = Math.min(startRow, endRow); row <= Math.max(startRow, endRow); row += 1) {
      for (let col = Math.min(startCol, endCol); col <= Math.max(startCol, endCol); col += 1) {
        this.mergeCellMetadata(col, row, protection ? { protection } : { protection: undefined })
      }
    }
    this.callback?.()
  }

  setCellValue(col: number, row: number, value: string, opts?: { validationOverride?: boolean }) {
    const normalizedValue = String(value ?? '')
    const lockedCell = this.firstLockedCellInRange(col, row, col, row)
    if (lockedCell) {
      this.emitProtectedCellEdit(lockedCell, 'Edit cell', normalizedValue)
      return
    }
    const validationRule = this.validationRuleForCell(col, row)
    const allowedValues = validationRule ? LIVE_FIXTURE_VALIDATION_OPTIONS[validationRule.id] ?? validationRule.values ?? [] : []
    if (
      validationRule &&
      opts?.validationOverride !== true &&
      normalizedValue &&
      !allowedValues.includes(normalizedValue)
    ) {
      this.mutationErrorCallback?.({
        requestId: `fixture_validation_${Date.now()}`,
        message: validationRule.error_message ?? 'The value is not allowed.',
        col,
        row,
        value: normalizedValue,
        retryWithValidationOverride: () => this.setCellValue(col, row, normalizedValue, { validationOverride: true }),
      })
      return
    }
    if (normalizedValue) this.cells.set(this.key(col, row), normalizedValue)
    else this.cells.delete(this.key(col, row))
    this.callback?.()
  }

  setCellFormat(col: number, row: number, format: CellFormat) {
    this.formats.set(this.key(col, row), { ...(this.getCellFormat(col, row) ?? {}), ...format })
    this.callback?.()
  }

  setRangeFormat(startCol: number, startRow: number, endCol: number, endRow: number, format: CellFormat) {
    const lockedCell = this.firstLockedCellInRange(startCol, startRow, endCol, endRow)
    if (lockedCell) {
      this.emitProtectedCellEdit(lockedCell, 'Format cells')
      return
    }
    for (let row = Math.min(startRow, endRow); row <= Math.max(startRow, endRow); row += 1) {
      for (let col = Math.min(startCol, endCol); col <= Math.max(startCol, endCol); col += 1) {
        this.setCellFormat(col, row, format)
      }
    }
  }

  clearRange(startCol: number, startRow: number, width: number, height: number) {
    const lockedCell = this.firstLockedCellInRange(startCol, startRow, startCol + width - 1, startRow + height - 1)
    if (lockedCell) {
      this.emitProtectedCellEdit(lockedCell, 'Clear contents')
      return
    }
    for (let row = startRow; row < startRow + height; row += 1) {
      for (let col = startCol; col < startCol + width; col += 1) {
        this.cells.delete(this.key(col, row))
      }
    }
    this.callback?.()
  }

  pasteData(startCol: number, startRow: number, text: string, opts?: { validationOverride?: boolean }) {
    const rows = parseFixtureTsv(text)
    if (rows.length <= 0) return
    const targetCol = Math.max(0, Math.floor(Number(startCol) || 0))
    const targetRow = Math.max(0, Math.floor(Number(startRow) || 0))
    const width = rows.reduce((max, row) => Math.max(max, row.length), 1)
    const height = rows.length
    const lockedCell = this.firstLockedCellInRange(targetCol, targetRow, targetCol + width - 1, targetRow + height - 1)
    if (lockedCell) {
      this.emitProtectedCellEdit(lockedCell, 'Paste cells')
      return
    }
    if (opts?.validationOverride !== true) {
      for (let rowOffset = 0; rowOffset < rows.length; rowOffset += 1) {
        const row = rows[rowOffset] ?? []
        for (let colOffset = 0; colOffset < row.length; colOffset += 1) {
          const value = String(row[colOffset] ?? '')
          const col = targetCol + colOffset
          const rowIndex = targetRow + rowOffset
          const validationRule = this.validationRuleForCell(col, rowIndex)
          const allowedValues = validationRule ? LIVE_FIXTURE_VALIDATION_OPTIONS[validationRule.id] ?? validationRule.values ?? [] : []
          if (validationRule && value && !allowedValues.includes(value)) {
            this.mutationErrorCallback?.({
              requestId: `fixture_bulk_validation_${Date.now()}`,
              message: validationRule.error_message ?? 'The value is not allowed.',
              col,
              row: rowIndex,
              operationLabel: 'Paste cells',
              targetRange: {
                sheet_id: LIVE_FIXTURE_SHEET_ID,
                start_row: targetRow,
                start_col: targetCol,
                end_row: targetRow + height - 1,
                end_col: targetCol + width - 1,
              },
              retryWithValidationOverride: () => this.pasteData(targetCol, targetRow, text, { validationOverride: true }),
            })
            return
          }
        }
      }
    }
    rows.forEach((row, rowOffset) => {
      row.forEach((value, colOffset) => {
        this.setCellValue(targetCol + colOffset, targetRow + rowOffset, value, opts)
      })
    })
  }

  fillData() {}
  moveData() {}
  copyData() {}
  hasEngineClipboard() { return false }
  getEngineClipboardPasteShape() { return null }
  pasteSpecialFromLastCopy() { return false }
  copyRangeTsv = async () => ''

  setNamedRangesCallback(callback: ((payload: SpreadsheetNamedRangesResponsePayload) => void) | null) {
    this.namedRangesCallback = callback
    if (callback) callback(this.namedRangesPayload())
  }

  setDefinedNamesCallback(callback: ((payload: SpreadsheetDefinedNamesResponsePayload) => void) | null) {
    this.definedNamesCallback = callback
    if (callback) callback(this.definedNamesPayload())
  }

  async getNamedRanges(): Promise<SpreadsheetNamedRangesResponsePayload> {
    return this.namedRangesPayload()
  }

  async getDefinedNames(): Promise<SpreadsheetDefinedNamesResponsePayload> {
    return this.definedNamesPayload()
  }

  private sameScope(a: SpreadsheetDefinedNameScope, b: SpreadsheetDefinedNameScope) {
    if (a.kind !== b.kind) return false
    return a.kind === 'workbook' || a.sheet_id === (b as Extract<SpreadsheetDefinedNameScope, { kind: 'sheet' }>).sheet_id
  }

  upsertDefinedName(draft: {
    name: string
    scope?: SpreadsheetDefinedNameScope
    refersTo?: string | null
    range?: SpreadsheetRangeAddress | null
    comment?: string | null
    hidden?: boolean
  }) {
    const name = String(draft.name ?? '').trim()
    if (!name) throw new Error('Defined name is required')
    const scope = draft.scope ?? { kind: 'workbook' as const }
    const refers_to = String(draft.refersTo ?? '').trim() || (draft.range ? '=A1' : '')
    this.definedNames = this.definedNames.filter((entry) => (
      entry.name.toLowerCase() !== name.toLowerCase() || !this.sameScope(entry.scope, scope)
    ))
    this.definedNames.push({
      name,
      scope,
      refers_to,
      ...(draft.range ? { range: draft.range } : null),
      ...(draft.comment ? { comment: draft.comment } : null),
      ...(draft.hidden === true ? { hidden: true } : null),
    })
    this.namedRangesCallback?.(this.namedRangesPayload())
    this.definedNamesCallback?.(this.definedNamesPayload())
    this.callback?.()
  }

  defineName(name: string, range: SpreadsheetRangeAddress) {
    this.upsertDefinedName({ name, scope: { kind: 'workbook' }, refersTo: '=A1', range })
  }

  deleteName(name: string, opts?: { scope?: SpreadsheetDefinedNameScope }) {
    const scope = opts?.scope ?? { kind: 'workbook' as const }
    this.definedNames = this.definedNames.filter((entry) => (
      entry.name.toLowerCase() !== String(name ?? '').trim().toLowerCase() || !this.sameScope(entry.scope, scope)
    ))
    this.namedRangesCallback?.(this.namedRangesPayload())
    this.definedNamesCallback?.(this.definedNamesPayload())
    this.callback?.()
  }

  async evaluateDraftFormula(formula: string) {
    const text = String(formula ?? '').trim()
    return { value: { kind: 'text' as const, value: text }, display_value: text }
  }

  setValidationRulesCallback(callback: ((payload: SpreadsheetValidationRulesResponsePayload) => void) | null) {
    this.validationRulesCallback = callback
    if (callback) callback(this.validationRulesPayload())
  }

  async getValidationRules(): Promise<SpreadsheetValidationRulesResponsePayload> {
    return this.validationRulesPayload()
  }

  async getValidationDropdownOptions(address: SpreadsheetCellAddress): Promise<SpreadsheetValidationDropdownOptionsResponsePayload> {
    const rule = this.validationRuleForCell(address.col, address.row) ?? LIVE_FIXTURE_PRODUCT_VALIDATION_RULE
    return {
      requestId: 'fixture-validation-options',
      workbookId: 'active_workbook',
      sheetId: LIVE_FIXTURE_SHEET_ID,
      workbookRevision: 1,
      workbook_id: 'active_workbook',
      workbook_revision: 1,
      sheet_id: LIVE_FIXTURE_SHEET_ID,
      address,
      validation_id: rule.id,
      rule,
      source_formula: rule.formula1 ?? null,
      source_ranges: rule.id === LIVE_FIXTURE_REGION_VALIDATION_RULE.id ? LIVE_FIXTURE_REGION_SOURCE_RANGES : [],
      options: LIVE_FIXTURE_VALIDATION_OPTIONS[rule.id] ?? rule.values ?? [],
      error: null,
    }
  }

  setMutationErrorCallback(callback: ((error: SpreadsheetMutationError) => void) | null) {
    this.mutationErrorCallback = callback
  }

  setFindCallback(callback: (matches: SpreadsheetFindMatch[]) => void) {
    this.findCallback = callback
  }

  async findText(query: string) {
    const needle = String(query ?? '').trim().toLowerCase()
    if (!needle) {
      this.findCallback?.([])
      return
    }
    const matches: SpreadsheetFindMatch[] = []
    for (const [key, value] of this.cells.entries()) {
      if (!value.toLowerCase().includes(needle)) continue
      const [col, row] = key.split(',').map(Number)
      matches.push({ col, row, data: { value } })
    }
    matches.sort((a, b) => a.row - b.row || a.col - b.col)
    this.findCallback?.(matches)
  }
}

export function ExcelLiveChromeFixture() {
  const sheetProtectionOptions = useMemo<SpreadsheetSheetProtectionOptions | null>(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    if (params.get('sheetProtection') !== '1') return null
    return {
      enabled: true,
      ...(params.get('selectLocked') === '0' ? { allow_select_locked_cells: false } : null),
      ...(params.get('selectUnlocked') === '0' ? { allow_select_unlocked_cells: false } : null),
    }
  }, [])
  const controller = useMemo(() => {
    const nextController = new GridController()
    nextController.setStreamer(new ExcelLiveFixtureStreamer(sheetProtectionOptions) as any)
    return nextController
  }, [sheetProtectionOptions])
  const [workbookName, setWorkbookName] = useState('Revenue Model')
  const [visualizeActionLog, setVisualizeActionLog] = useState<string>('ready')
  const [sheetActionLog, setSheetActionLog] = useState<string>('sheet-ready')
  const [structuralActionLog, setStructuralActionLog] = useState<string>('structural-ready')
  const [activeCreatorPanel, setActiveCreatorPanel] = useState<string>('none')
  const [backgroundMap, setBackgroundMap] = useState<(typeof BACKGROUND_MAP_OPTIONS)[number]>('normal')
  const mapCommandsEnabled = useMemo(() => (
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('map') === '1'
  ), [])

  const recordVisualizeAction = useCallback((id: string, panel: string = id) => {
    setVisualizeActionLog(id)
    setActiveCreatorPanel(panel)
  }, [])

  const visualizeActions = useMemo<ExcelVisualizeCommandHandlers>(() => ({
    'creator-home': {
      onClick: () => recordVisualizeAction('creator-home', 'home'),
      enabled: true,
    },
    'import-workbook': {
      onClick: () => recordVisualizeAction('import-workbook', 'import'),
      enabled: true,
    },
    'save-workbook': {
      onClick: () => recordVisualizeAction('save-workbook', 'save'),
      enabled: true,
    },
    'delete-workbook': {
      onClick: () => recordVisualizeAction('delete-workbook', 'delete'),
      enabled: true,
    },
    'creator-undo': {
      onClick: () => recordVisualizeAction('creator-undo', 'history'),
      enabled: true,
    },
    'creator-redo': {
      onClick: () => recordVisualizeAction('creator-redo', 'history'),
      enabled: true,
    },
    'add-visualization': {
      onClick: () => recordVisualizeAction('add-visualization', 'add-viz'),
      enabled: true,
      visible: true,
    },
    'dashboard-actions': {
      onClick: () => recordVisualizeAction('dashboard-actions', 'actions'),
      enabled: true,
      visible: true,
    },
    'map-layers': {
      onClick: () => recordVisualizeAction('map-layers', 'map-layers'),
      enabled: mapCommandsEnabled,
      disabledReason: 'Only available for map visualizations',
    },
    'map-background': {
      enabled: mapCommandsEnabled,
      disabledReason: 'Only available for map visualizations',
      menuItems: BACKGROUND_MAP_OPTIONS.map((value) => ({
        id: value,
        label: value[0].toUpperCase() + value.slice(1),
        selected: backgroundMap === value,
        onClick: () => {
          setBackgroundMap(value)
          recordVisualizeAction(`map-background:${value}`, 'map-background')
        },
      })),
    },
    'map-options': {
      onClick: () => recordVisualizeAction('map-options', 'map-options'),
      enabled: mapCommandsEnabled,
      disabledReason: 'Only available for map visualizations',
    },
    'developer-tools': {
      onClick: () => recordVisualizeAction('developer-tools', 'dev'),
      enabled: true,
      active: activeCreatorPanel === 'dev',
    },
    'ai-panel': {
      onClick: () => recordVisualizeAction('ai-panel', 'ai'),
      enabled: true,
      active: activeCreatorPanel === 'ai',
    },
  }), [activeCreatorPanel, backgroundMap, mapCommandsEnabled, recordVisualizeAction])

  useEffect(() => {
    ;(window as any).__FG_EXCEL_LIVE_CONTROLLER__ = controller
    return () => {
      if ((window as any).__FG_EXCEL_LIVE_CONTROLLER__ === controller) {
        delete (window as any).__FG_EXCEL_LIVE_CONTROLLER__
      }
    }
  }, [controller])

  useEffect(() => {
    const selectedRange = { col: 1, row: 1, endCol: 4, endRow: 4 }
    controller.setDimensions(12, 1000, { notifyStreamer: false })
    controller.setSelection(selectedRange)

    const raf = window.requestAnimationFrame(() => {
      controller.setSelection(selectedRange)
      controller.notifySelectionChange()
      controller.requestRender()
    })

    return () => {
      window.cancelAnimationFrame(raf)
    }
  }, [controller])

  return (
    <main
      className="fg-excel-chrome fg-excel-live-fixture"
      data-testid="fg-excel-live-shell-fixture"
    >
      <div className="fg-excel-live-fixture-grid">
        <div className="fg-excel-live-fixture-body">
          <AppHTMLHeaders
            controller={controller}
            colCount={12}
            rowCount={1000}
            onAddRows={() => {}}
            sheetName="Sales Model"
            workbookName={workbookName}
            onWorkbookNameChange={setWorkbookName}
            visualizeActions={visualizeActions}
            onColumnAction={(action, selection) => {
              setStructuralActionLog(`column:${action}:${selection.startCol}-${selection.endCol}`)
            }}
            onRowAction={(action, selection) => {
              setStructuralActionLog(`row:${action}:${selection.startRow}-${selection.endRow}`)
            }}
            embedded
            showToolbar
            showFormulaBar
          />
        </div>
        <div
          className="fg-excel-visualize-fixture-panel"
          data-testid="fg-excel-visualize-action-log"
          data-last-action={visualizeActionLog}
          data-active-panel={activeCreatorPanel}
          data-last-sheet-action={sheetActionLog}
          data-last-structural-action={structuralActionLog}
          title={`Last Visualize action: ${visualizeActionLog}`}
        >
          Last Visualize action: <strong>{visualizeActionLog}</strong>
        </div>
        <ExcelSheetTabs
          tabs={[
            {
              id: 'sales-model',
              name: 'Sales Model',
              kind: 'spreadsheet',
              sourceKind: 'spreadsheet',
              active: true,
              onSelect: () => {},
              spreadsheetActions: {
                enabled: true,
                canDuplicate: true,
                canMoveLeft: false,
                canMoveRight: true,
                canDelete: true,
                canHide: true,
                onRename: () => {},
                onDuplicate: () => setSheetActionLog('duplicate:sales-model'),
                onMoveLeft: () => {},
                onMoveRight: () => {},
                onHide: () => setSheetActionLog('hide:sales-model'),
                onDelete: () => {},
              },
            },
            {
              id: 'inputs',
              name: 'Inputs',
              kind: 'spreadsheet',
              sourceKind: 'spreadsheet',
              onSelect: () => {},
              spreadsheetActions: {
                enabled: true,
                canDuplicate: true,
                canMoveLeft: true,
                canMoveRight: true,
                canDelete: true,
                canHide: true,
                onRename: () => {},
                onDuplicate: () => setSheetActionLog('duplicate:inputs'),
                onMoveLeft: () => {},
                onMoveRight: () => {},
                onHide: () => setSheetActionLog('hide:inputs'),
                onDelete: () => {},
              },
            },
            {
              id: 'forecast',
              name: 'Forecast',
              kind: 'spreadsheet',
              sourceKind: 'spreadsheet',
              onSelect: () => {},
              spreadsheetActions: {
                enabled: true,
                canDuplicate: true,
                canMoveLeft: true,
                canMoveRight: false,
                canDelete: true,
                canHide: true,
                onRename: () => {},
                onDuplicate: () => setSheetActionLog('duplicate:forecast'),
                onMoveLeft: () => {},
                onMoveRight: () => {},
                onHide: () => setSheetActionLog('hide:forecast'),
                onDelete: () => {},
              },
            },
          ]}
          hiddenSpreadsheetTabs={[{
            id: 'archive-hidden',
            name: 'Archive',
            onUnhide: () => setSheetActionLog('unhide:archive'),
          }]}
          dataSourceActive={false}
          onSelectDataSource={() => {}}
          onAddSpreadsheetSheet={() => {}}
          canAddSpreadsheetSheet
        />
      </div>
    </main>
  )
}
